import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Box, Typography, Button, IconButton, Alert, Card, CardContent, Fade, LinearProgress, Drawer, CircularProgress } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import FavoriteIcon from '@mui/icons-material/Favorite';
import FavoriteBorderIcon from '@mui/icons-material/FavoriteBorder';
import ShareIcon from '@mui/icons-material/Share';
import QrCodeScannerIcon from '@mui/icons-material/QrCodeScanner';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import DownloadIcon from '@mui/icons-material/Download';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import IosShareIcon from '@mui/icons-material/IosShare';
import CloseIcon from '@mui/icons-material/Close';
import ShoppingBagOutlinedIcon from '@mui/icons-material/ShoppingBagOutlined';
import { performVirtualTryOn } from '../services/segmind';
import { getProductById, getAllProducts } from '../services/productService';
import { trackInteraction } from '../services/customerService';
import { trackTryOn } from '../services/analyticsService';
import { apiCall } from '../api/config';
import { getCaptchaToken } from '../services/turnstile';
import { getCachedTryOn, clearCachedTryOn } from '../services/tryOnCache';
import { scoreAgainstArmocromia, productSeasonTags } from '../utils/armocromiaMatcher';

const TRYON_LOADING_STEPS = [
  { at: 0, text: 'Preparazione della foto...' },
  { at: 8, text: 'Analisi del tono della pelle...' },
  { at: 18, text: 'Analisi del sottotono (caldo/freddo)...' },
  { at: 28, text: 'Determinazione stagione armocromia...' },
  { at: 40, text: 'Generazione palette colori personalizzata...' },
  { at: 52, text: 'Preparazione prova virtuale...' },
  { at: 65, text: 'Applicazione del capo alla tua figura...' },
  { at: 78, text: 'Regolazione ombre e vestibilità...' },
  { at: 88, text: 'Rifinitura del risultato finale...' },
  { at: 95, text: 'Quasi pronto...' },
];

const CURIOSITA = [
  'Ognuno di noi ha dei colori che lo fanno sembrare subito più riposato e luminoso. L\'armocromia serve proprio a trovarli!',
  'La tua pelle ha un "sottotono" che non cambia mai, nemmeno con l\'abbronzatura. I colori che ti stanno bene restano sempre gli stessi.',
  'Trucco veloce: guarda le vene del polso. Se tendono al verde hai un sottotono caldo, se tendono al blu hai un sottotono freddo.',
  'Indossare i colori giusti illumina il viso, riduce le occhiaie e rende la pelle più uniforme. Quelli sbagliati fanno l\'effetto opposto!',
  'L\'armocromia non vale solo per i vestiti: anche il trucco, i gioielli, le montature degli occhiali e il colore dei capelli seguono le stesse regole.',
  'Marilyn Monroe stava benissimo con i colori caldi e vivaci. Audrey Hepburn invece con il nero e il bianco puro. Ognuno ha la sua palette!',
  'Piccola regola per i gioielli: se hai un sottotono caldo, l\'oro giallo ti valorizza. Se ce l\'hai freddo, meglio l\'argento o il platino.',
  'Più della metà delle persone sceglie istintivamente i colori opposti a quelli che li valorizzano. Per questo un\'analisi fa davvero la differenza.',
  'In Giappone e Corea l\'analisi dei colori personali è diventata una mania: ci sono studi dedicati in ogni quartiere delle grandi città!',
  'L\'idea di abbinare i colori alle persone nasce negli anni \'30, quando un artista svizzero notò che i suoi studenti dipingevano meglio usando i colori in armonia col proprio aspetto.',
  'Esistono ben 12 varianti di palette personalizzate, non solo le 4 stagioni base. Tra poco scoprirai qual è la tua!',
  'Un colore "sbagliato" non è brutto in sé: semplicemente non è in armonia con le tue tonalità naturali. Quello giusto ti fa brillare.',
];

// Personalized tips for each armocromia season { icon, title, text } — female-oriented
const CONSIGLI_PER_STAGIONE_DONNA = {
  'Primavera': [
    { icon: '🖤', title: 'Il nero', text: 'Evita il nero puro vicino al viso: ti farebbe sembrare stanca. Sostituiscilo con il marrone cioccolato o il blu notte.' },
    { icon: '💄', title: 'Il tuo rossetto', text: 'Il corallo è il tuo portafortuna: illumina il viso in un attimo e ti dona una luminosità naturale.' },
    { icon: '✨', title: 'Abiti da sera', text: 'Per le occasioni importanti, osa con un abito verde acqua o turchese caldo: sono colori che ti fanno brillare.' },
    { icon: '👖', title: 'I jeans giusti', text: 'Preferisci i lavaggi chiari e caldi: il denim scuro e freddo ti spegne.' },
    { icon: '🤍', title: 'Il tuo bianco', text: 'Il bianco panna e l\'avorio sono molto più adatti a te del bianco ottico: riscaldano il viso invece di appiattirlo.' },
    { icon: '👁️', title: 'Make-up occhi', text: 'Punta su ombretti color pesca, bronzo caldo e verde oliva. Gli occhi si accendono subito.' },
    { icon: '💇', title: 'Colore capelli', text: 'Ti stanno bene con riflessi dorati, ramati o miele. Evita il cenere o il freddo.' },
    { icon: '👜', title: 'Borsa must-have', text: 'Una borsa cammello o cuoio caldo è un investimento sicuro: si abbina a quasi tutto il tuo guardaroba.' },
    { icon: '🌸', title: 'Il tuo rosa', text: 'Il rosa confetto e il salmone ti valorizzano molto più del rosa shocking freddo.' },
    { icon: '👓', title: 'Occhiali', text: 'Preferisci montature color tartaruga, corno o oro rispetto a quelle nere o argento.' },
    { icon: '⚪', title: 'Neutri', text: 'Evita i grigi medi: ti fanno sembrare spenta. Scegli beige caldo o cammello.' },
    { icon: '🌞', title: 'Il giallo sole', text: 'È uno dei tuoi colori migliori, anche se ti spaventa: provalo almeno con un accessorio.' },
    { icon: '💋', title: 'Rossetto da evitare', text: 'Evita rossetti con undertone blu (fucsia, mora): scegli sempre toni caldi.' },
    { icon: '💼', title: 'Look ufficio', text: 'Il blu navy ti sta bene ma deve essere un navy caldo, non freddo.' },
    { icon: '🌱', title: 'Verde menta', text: 'Una scelta azzeccata per la primavera-estate: fresco ma ancora caldo.' },
    { icon: '🌆', title: 'Outfit da sera', text: 'Un abito color terracotta o arancio bruciato ti regala un look sofisticato e solare.' },
    { icon: '🎨', title: 'Colori vivaci', text: 'Non temerli: il tuo incarnato li supporta bene. Osa con corallo, turchese, verde lime.' },
    { icon: '🫧', title: 'Fondotinta', text: 'Ti serve un fondotinta con sottotono pesca/oro, non rosato.' }
  ],
  'Estate': [
    { icon: '🖤', title: 'Il nero', text: 'Ti appesantisce: sostituiscilo con il blu notte, il grigio antracite o il viola scuro.' },
    { icon: '💄', title: 'Il tuo rossetto', text: 'Un rosa freddo o un lilla delicato. Evita i toni arancio.' },
    { icon: '✨', title: 'Abiti da sera', text: 'Un abito color lavanda o malva ti regala un\'aura raffinata ed elegante.' },
    { icon: '👖', title: 'I jeans giusti', text: 'Preferisci lavaggi freddi e chiari: evita i denim con tonalità gialle o calde.' },
    { icon: '🤍', title: 'Il tuo bianco', text: 'Il bianco latte o il ghiaccio, non il bianco caldo o avorio.' },
    { icon: '👁️', title: 'Make-up occhi', text: 'Ombretti freddi: grigio fumo, lavanda, blu polvere, rosa antico.' },
    { icon: '💇', title: 'Colore capelli', text: 'Riflessi cenere, platino o marrone freddo. Evita i toni ramati.' },
    { icon: '👜', title: 'Borsa must-have', text: 'Una borsa grigio perla o tortora freddo si abbina a tutta la tua palette.' },
    { icon: '🌸', title: 'Il tuo rosa', text: 'Rosa cipria e rosa polvere sono perfetti, non quelli caldi e saturi.' },
    { icon: '👓', title: 'Occhiali', text: 'Preferisci montature grigie, blu o argento. Evita il tartaruga caldo.' },
    { icon: '⚪', title: 'Neutri', text: 'I grigi ti stanno benissimo, specialmente nelle sfumature polverose.' },
    { icon: '☀️', title: 'Da evitare', text: 'Evita il giallo senape e l\'arancio: ti fanno sembrare sbiadita.' },
    { icon: '🎭', title: 'Contrasti', text: 'Il contrasto alto ti appesantisce. Preferisci look tono-su-tono o transizioni morbide.' },
    { icon: '💼', title: 'Look ufficio', text: 'Il blu acciaio e il blu polvere sono più valorizzanti del navy classico.' },
    { icon: '🌿', title: 'Verde salvia', text: 'Verde salvia e verde menta freddo sono scelte eccellenti per l\'estate.' },
    { icon: '🌆', title: 'Outfit da sera', text: 'Un abito color prugna chiaro o blu polvere ti dona un\'eleganza raffinata.' },
    { icon: '🫧', title: 'Fondotinta', text: 'Ti serve un fondotinta rosato, non pesca.' },
    { icon: '🎨', title: 'Saturazione', text: 'Evita i colori fluo e saturi: ti "slavano" il viso. Punta su tonalità attenuate.' }
  ],
  'Autunno': [
    { icon: '🖤', title: 'Il nero', text: 'Non è il tuo colore: sostituiscilo con marrone cioccolato fondente o verde bosco.' },
    { icon: '💄', title: 'Il tuo rossetto', text: 'Un marrone caldo, un mattone o un terracotta intenso ti valorizza molto.' },
    { icon: '✨', title: 'Abiti da sera', text: 'Osa con bordeaux, ruggine o verde foresta: colori profondi e regali.' },
    { icon: '👖', title: 'I jeans giusti', text: 'Lavaggi medi e caldi, con possibili sfumature rossastre.' },
    { icon: '🤍', title: 'Il tuo bianco', text: 'L\'avorio e il beige caldo. Il bianco ottico ti fa sembrare grigia.' },
    { icon: '👁️', title: 'Make-up occhi', text: 'Ombretti color oro, bronzo, verde oliva, marrone caldo, ramato.' },
    { icon: '💇', title: 'Colore capelli', text: 'Riflessi ramati, caramello, castano caldo o rosso esplodono su di te.' },
    { icon: '👜', title: 'Borsa must-have', text: 'Una borsa in cuoio naturale o cognac: invecchia bene e ti sta sempre bene.' },
    { icon: '🌸', title: 'Il tuo rosa', text: 'Rosa antico, pesca tenue, terracotta chiara: sempre tonalità calme e calde.' },
    { icon: '👓', title: 'Occhiali', text: 'Montature tartaruga, cuoio, oro antico. Evita il nero e l\'argento.' },
    { icon: '⚪', title: 'Neutri', text: 'Il grigio tortora caldo è il tuo neutro migliore. Evita i grigi freddi.' },
    { icon: '☀️', title: 'Il giallo senape', text: 'Giallo senape, senape scuro e ocra sono tra i colori che ti donano di più.' },
    { icon: '🧥', title: 'Monocromatico', text: 'Outfit monocromatici in toni caldi (cammello + moro + cuoio) = eleganza.' },
    { icon: '💼', title: 'Look ufficio', text: 'Sostituisci il classico blu navy con il marrone scuro o il verde petrolio.' },
    { icon: '🌿', title: 'Verdi', text: 'Verde oliva e kaki scuro sono tra i tuoi colori più valorizzanti.' },
    { icon: '🌆', title: 'Outfit da sera', text: 'Un abito color arancio bruciato o rosso mattone è sensazionale.' },
    { icon: '🫧', title: 'Fondotinta', text: 'Il tuo sottotono caldo richiede un fondotinta dorato o olivastro, non rosato.' },
    { icon: '🎨', title: 'Pastelli', text: 'Evita i colori pastello sbiaditi: ti "lavano" il viso. Preferisci tonalità profonde.' }
  ],
  'Inverno': [
    { icon: '🖤', title: 'Il nero', text: 'È uno dei tuoi colori base perfetti: lo porti come pochi altri. Abbinalo a bianco ottico o rosso vivo.' },
    { icon: '💄', title: 'Il tuo rossetto', text: 'Un rosso cremisi, un fucsia freddo o una prugna scura intensa.' },
    { icon: '✨', title: 'Abiti da sera', text: 'Blu royal, verde smeraldo o viola saturo: sei spettacolare.' },
    { icon: '👖', title: 'I jeans giusti', text: 'Lavaggi scuri e freddi. Evita i denim chiari e caldi.' },
    { icon: '🤍', title: 'Il tuo bianco', text: 'Il bianco ottico è uno dei tuoi migliori amici: ti illumina il viso.' },
    { icon: '👁️', title: 'Make-up occhi', text: 'Ombretti neri, grigio fumo, blu notte, viola intenso, argento brillante.' },
    { icon: '💇', title: 'Colore capelli', text: 'Nero corvino, castano scuro, platino, cenere: toni scurissimi e freddi.' },
    { icon: '👜', title: 'Borsa must-have', text: 'Una borsa nera è un investimento eterno: funziona con il 90% dei tuoi outfit.' },
    { icon: '🌸', title: 'Il tuo rosa', text: 'Solo quelli freddi e saturi: fucsia vivo o magenta.' },
    { icon: '👓', title: 'Occhiali', text: 'Montature nere, argento o trasparenti. Evita il tartaruga caldo.' },
    { icon: '⚪', title: 'Neutri', text: 'Grigio antracite e grigio acciaio sono i tuoi neutri perfetti.' },
    { icon: '☀️', title: 'Da evitare', text: 'Evita giallo senape, beige caldo e marrone cuoio: ti sbiadiscono.' },
    { icon: '🎭', title: 'Contrasti', text: 'Il contrasto alto è la tua forza: abbina colori scuri a chiari per effetti drammatici.' },
    { icon: '💼', title: 'Look ufficio', text: 'Blu notte saturo > navy classico. Abbinalo al bianco ottico.' },
    { icon: '🌿', title: 'Verdi', text: 'Verde smeraldo e teal profondo: tra i colori che ti donano di più.' },
    { icon: '🌆', title: 'Outfit da sera', text: 'Un abito color rubino, zaffiro o nero con accessori argentati è perfetto.' },
    { icon: '🫧', title: 'Fondotinta', text: 'Il tuo sottotono freddo richiede un fondotinta rosato o beige freddo. Mai pesca.' },
    { icon: '🎨', title: 'Saturazione', text: 'A te servono colori puri e decisi, non attenuati. Evita i pastelli sbiaditi.' }
  ]
};

// Personalized tips for each armocromia season { icon, title, text } — male-oriented
const CONSIGLI_PER_STAGIONE_UOMO = {
  'Primavera': [
    { icon: '🖤', title: 'Il nero', text: 'Evita il nero vicino al viso: ti fa sembrare stanco. Sostituiscilo con il blu navy caldo o il marrone cioccolato.' },
    { icon: '👕', title: 'La camicia bianca', text: 'Preferisci il bianco panna o avorio: il bianco ottico ti spegne l\'incarnato.' },
    { icon: '👖', title: 'I jeans giusti', text: 'Lavaggi chiari e caldi. Il denim scuro e freddo ti spegne.' },
    { icon: '👔', title: 'La cravatta', text: 'Corallo, verde oliva caldo, arancio ruggine: i toni caldi ti valorizzano molto più di quelli freddi.' },
    { icon: '⌚', title: 'Orologio', text: 'Cinturino in cuoio cognac o cammello, meglio dell\'acciaio freddo.' },
    { icon: '🧥', title: 'Giacca/blazer', text: 'Cammello, beige caldo o verde militare sono le tue scelte vincenti.' },
    { icon: '👞', title: 'Scarpe', text: 'Stringate o mocassini in cuoio cognac: evita il nero laccato.' },
    { icon: '👓', title: 'Occhiali', text: 'Montature tartaruga, corno o oro antico, non nere o argento.' },
    { icon: '💼', title: 'Look ufficio', text: 'Sostituisci il grigio freddo con un blu navy caldo o un marrone scuro.' },
    { icon: '🧵', title: 'Maglione', text: 'Cachemire color cammello, verde oliva o terra di Siena.' },
    { icon: '💇', title: 'Capelli e barba', text: 'Riflessi dorati, ramati o miele. Evita il cenere o i toni freddi.' },
    { icon: '🔗', title: 'Cintura', text: 'Cuoio naturale o cognac. Mai nera lucida vicino ai pantaloni chiari.' },
    { icon: '👖', title: 'Chinos', text: 'Sabbia, cammello, oliva chiaro: i tuoi pantaloni casual perfetti.' },
    { icon: '🌿', title: 'Verde militare', text: 'Uno dei tuoi must: sta d\'incanto in polo, camicia o giacca.' },
    { icon: '🎽', title: 'Polo', text: 'Corallo, verde acqua e giallo ocra si accendono sul tuo incarnato.' },
    { icon: '🎨', title: 'Contrasti', text: 'Evita il bianco-nero netto. Preferisci transizioni calde come cammello-moro.' },
    { icon: '🧣', title: 'Sciarpa', text: 'Tonalità calde: ruggine, ocra, cammello o verde oliva.' },
    { icon: '🤵', title: 'Cerimonia', text: 'Abito grigio tortora caldo o blu navy caldo: il nero puro ti appesantisce.' }
  ],
  'Estate': [
    { icon: '🖤', title: 'Il nero', text: 'Ti appesantisce: scegli blu notte, grigio antracite o viola scuro.' },
    { icon: '👕', title: 'La camicia bianca', text: 'Bianco ottico o azzurro polvere. Mai avorio o panna caldo.' },
    { icon: '👖', title: 'I jeans giusti', text: 'Lavaggi freddi, dal medio al chiaro. Evita le sfumature gialle.' },
    { icon: '👔', title: 'La cravatta', text: 'Blu polvere, lavanda, bordeaux freddo. Evita arancio e senape.' },
    { icon: '⌚', title: 'Orologio', text: 'Acciaio o argento con cinturino blu o grigio. Evita il cuoio cognac.' },
    { icon: '🧥', title: 'Giacca/blazer', text: 'Grigio antracite, blu polvere o blu acciaio ti stanno meglio dei toni caldi.' },
    { icon: '👞', title: 'Scarpe', text: 'Stringate nere o grigie, mocassini blu navy. Evita il cognac.' },
    { icon: '👓', title: 'Occhiali', text: 'Montature blu, grigie o argento. Evita il tartaruga caldo.' },
    { icon: '💼', title: 'Look ufficio', text: 'Grigio medio, blu acciaio, navy freddo: i tuoi neutri da ufficio.' },
    { icon: '🧵', title: 'Maglione', text: 'Cachemire grigio perla, blu polvere o malva.' },
    { icon: '💇', title: 'Capelli e barba', text: 'Toni cenere, castano freddo o platino. Evita il ramato.' },
    { icon: '🔗', title: 'Cintura', text: 'Cuoio grigio o nero freddo. Evita le tonalità calde.' },
    { icon: '👖', title: 'Chinos', text: 'Grigio tortora freddo o blu polvere: eleganza leggera.' },
    { icon: '🌿', title: 'Verde salvia', text: 'Perfetto in polo o camicia. Evita il verde oliva caldo.' },
    { icon: '🎽', title: 'Polo', text: 'Rosa polvere, azzurro polvere, lavanda: colori freddi e delicati.' },
    { icon: '🎨', title: 'Contrasti', text: 'Il contrasto alto ti appesantisce. Preferisci tono-su-tono in toni freddi.' },
    { icon: '🧣', title: 'Sciarpa', text: 'Grigio perla, blu polvere, malva o prugna chiaro.' },
    { icon: '🤵', title: 'Cerimonia', text: 'Abito blu notte o grigio scuro freddo: mai marrone caldo.' }
  ],
  'Autunno': [
    { icon: '🖤', title: 'Il nero', text: 'Non è il tuo colore: sostituiscilo con marrone cioccolato fondente o verde bosco.' },
    { icon: '👕', title: 'La camicia bianca', text: 'Avorio o beige caldo. Il bianco ottico ti fa sembrare grigio.' },
    { icon: '👖', title: 'I jeans giusti', text: 'Lavaggi medi e caldi, con possibili sfumature rossastre. I jeans vissuti sono tuoi amici.' },
    { icon: '👔', title: 'La cravatta', text: 'Bordeaux, ruggine, verde foresta, senape: colori profondi e caldi.' },
    { icon: '⌚', title: 'Orologio', text: 'Cinturino in cuoio cognac o testa di moro, cassa in acciaio brunito o oro.' },
    { icon: '🧥', title: 'Giacca/blazer', text: 'Cammello, marrone cioccolato, verde bosco: colori profondi e caldi.' },
    { icon: '👞', title: 'Scarpe', text: 'Stringate cognac o testa di moro. Mai nero laccato.' },
    { icon: '👓', title: 'Occhiali', text: 'Montature cuoio, tartaruga scura o oro antico. Evita nero e argento.' },
    { icon: '💼', title: 'Look ufficio', text: 'Marrone scuro o verde petrolio invece del classico blu navy.' },
    { icon: '🧵', title: 'Maglione', text: 'Cachemire color cammello, ruggine o verde bosco: texture calde e ricche.' },
    { icon: '💇', title: 'Capelli e barba', text: 'Riflessi ramati, caramello o castano caldo esplodono su di te.' },
    { icon: '🔗', title: 'Cintura', text: 'Cuoio naturale, cognac o testa di moro. Invecchia bene e ti sta sempre bene.' },
    { icon: '👖', title: 'Chinos', text: 'Cachi scuro, tabacco, senape: i tuoi pantaloni casual perfetti.' },
    { icon: '🌿', title: 'Verde oliva', text: 'Tra i tuoi colori top: usalo in giacca, camicia o polo.' },
    { icon: '🎽', title: 'Polo', text: 'Senape, terracotta, arancio bruciato: colori che ti accendono.' },
    { icon: '🧥', title: 'Monocromatico', text: 'Outfit tutto in toni terra (cammello + moro + cuoio) = eleganza assoluta.' },
    { icon: '🧣', title: 'Sciarpa', text: 'Lana in ruggine, senape o cammello. Texture calde e materiche.' },
    { icon: '🤵', title: 'Cerimonia', text: 'Abito blu notte caldo o marrone scuro. Evita il nero puro: ti spegne.' }
  ],
  'Inverno': [
    { icon: '🖤', title: 'Il nero', text: 'È il tuo colore base perfetto: lo porti come pochi. Abbinalo al bianco ottico o a un rosso vivo.' },
    { icon: '👕', title: 'La camicia bianca', text: 'Bianco ottico puro: è uno dei tuoi migliori amici, ti illumina il viso.' },
    { icon: '👖', title: 'I jeans giusti', text: 'Lavaggi scuri e freddi. Evita i chiari caldi o sfumati.' },
    { icon: '👔', title: 'La cravatta', text: 'Rosso cremisi, blu royal, viola saturo: osa con colori puri e decisi.' },
    { icon: '⌚', title: 'Orologio', text: 'Acciaio lucido o argento, cinturino nero o maglia metallica.' },
    { icon: '🧥', title: 'Giacca/blazer', text: 'Nero, grigio antracite, blu notte saturo: i tuoi cavalli di battaglia.' },
    { icon: '👞', title: 'Scarpe', text: 'Stringate nere lucide: il classico che ti sta sempre bene.' },
    { icon: '👓', title: 'Occhiali', text: 'Montature nere, argento o trasparenti. Evita il tartaruga caldo.' },
    { icon: '💼', title: 'Look ufficio', text: 'Blu notte saturo con camicia bianco ottico = look da copertina.' },
    { icon: '🧵', title: 'Maglione', text: 'Cachemire nero, bianco ottico o rosso rubino: colori puri, mai sfumati.' },
    { icon: '💇', title: 'Capelli e barba', text: 'Nero corvino, castano scuro freddo o cenere. Evita il ramato.' },
    { icon: '🔗', title: 'Cintura', text: 'Nero lucido o grigio acciaio. Mai cuoio cognac.' },
    { icon: '👖', title: 'Chinos', text: 'Grigio antracite o blu notte: i tuoi neutri più forti.' },
    { icon: '🌿', title: 'Verde smeraldo', text: 'Uno dei tuoi colori più valorizzanti: usalo senza timore.' },
    { icon: '🎽', title: 'Polo', text: 'Bianco ottico, fucsia freddo, blu royal: colori decisi e saturi.' },
    { icon: '🎨', title: 'Contrasti', text: 'Il contrasto alto è la tua forza: bianco ottico su nero o rosso su nero.' },
    { icon: '🧣', title: 'Sciarpa', text: 'Tonalità sature e fredde: rubino, smeraldo, zaffiro o grigio antracite.' },
    { icon: '🤵', title: 'Cerimonia', text: 'Abito nero puro o blu notte saturo con camicia bianco ottico. Spettacolare.' }
  ]
};

// Shopify CDN serves sized thumbnails natively — ~35KB instead of ~800KB.
function getShopifyThumb(url, width = 400) {
  if (!url || typeof url !== 'string') return url;
  if (!url.includes('cdn.shopify.com')) return url;
  return url.replace(/(\.(jpg|jpeg|png|webp|gif))(\?|$)/i, `_${width}x$1$3`);
}

function shuffleArray(arr) {
  const shuffled = [...arr];
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  return shuffled;
}

function TryOnResult() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const productIdParam = searchParams.get('p');
  const initialized = useRef(false);
  const lastLoadedProduct = useRef(null);

  const [product, setProduct] = useState(null);
  const [tryOnImage, setTryOnImage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingMessage, setLoadingMessage] = useState(TRYON_LOADING_STEPS[0].text);
  const [isSaved, setIsSaved] = useState(false);
  const [error, setError] = useState(null);
  const [armocromia, setArmocromia] = useState(null);
  const [recommendations, setRecommendations] = useState([]);
  const [contactSent, setContactSent] = useState(false);
  const [progress, setProgress] = useState(0);
  const [curiositaText, setCuriositaText] = useState('');
  const [curiositaVisible, setCuriositaVisible] = useState(true);
  const [userPhoto, setUserPhoto] = useState(null);
  const [tryOnPending, setTryOnPending] = useState(false);
  const [userContactInfo, setUserContactInfo] = useState(null);
  const [consiglioIdx, setConsiglioIdx] = useState(0);
  const [consiglioVisible, setConsiglioVisible] = useState(true);
  const [consiglioDirection, setConsiglioDirection] = useState('next'); // 'next' | 'prev'
  const [shareMenuOpen, setShareMenuOpen] = useState(false);
  const [tryOnImageFile, setTryOnImageFile] = useState(null);
  const [sharePendingAction, setSharePendingAction] = useState(null); // 'save' | 'whatsapp' | 'share' | null
  const [shareError, setShareError] = useState(null);
  const [recDialogProduct, setRecDialogProduct] = useState(null);
  const progressInterval = useRef(null);
  const curiositaInterval = useRef(null);
  const consiglioInterval = useRef(null);
  const shuffledCuriosita = useRef(shuffleArray(CURIOSITA));
  const shuffledConsigli = useRef([]);
  const curiositaIdxRef = useRef(0);

  const startProgressBar = () => {
    const duration = 60000;
    const intervalMs = 300;
    const maxProgress = 95;
    let elapsed = 0;

    progressInterval.current = setInterval(() => {
      elapsed += intervalMs;
      const raw = (elapsed / duration) * maxProgress;
      const eased = maxProgress * (1 - Math.pow(1 - Math.min(raw / maxProgress, 1), 2.5));
      const currentProgress = Math.min(eased, maxProgress);

      setProgress(currentProgress);

      for (let i = TRYON_LOADING_STEPS.length - 1; i >= 0; i--) {
        if (currentProgress >= TRYON_LOADING_STEPS[i].at) {
          setLoadingMessage(TRYON_LOADING_STEPS[i].text);
          break;
        }
      }
    }, intervalMs);

    // Curiosità: ordine casuale, cambia ogni 8s con fade
    shuffledCuriosita.current = shuffleArray(CURIOSITA);
    curiositaIdxRef.current = 0;
    setCuriositaText(shuffledCuriosita.current[0]);
    setCuriositaVisible(true);
    curiositaInterval.current = setInterval(() => {
      setCuriositaVisible(false);
      setTimeout(() => {
        curiositaIdxRef.current = (curiositaIdxRef.current + 1) % shuffledCuriosita.current.length;
        setCuriositaText(shuffledCuriosita.current[curiositaIdxRef.current]);
        setCuriositaVisible(true);
      }, 400);
    }, 8000);
  };

  const stopProgressBar = () => {
    if (progressInterval.current) {
      clearInterval(progressInterval.current);
      progressInterval.current = null;
    }
    if (curiositaInterval.current) {
      clearInterval(curiositaInterval.current);
      curiositaInterval.current = null;
    }
    setProgress(100);
  };

  // Navigazione manuale curiosità (resetta il timer automatico)
  const goToCuriosita = (direction) => {
    if (curiositaInterval.current) {
      clearInterval(curiositaInterval.current);
    }
    setCuriositaVisible(false);
    setTimeout(() => {
      const len = shuffledCuriosita.current.length;
      curiositaIdxRef.current = (curiositaIdxRef.current + direction + len) % len;
      setCuriositaText(shuffledCuriosita.current[curiositaIdxRef.current]);
      setCuriositaVisible(true);
    }, 300);
    // Riavvia timer automatico
    curiositaInterval.current = setInterval(() => {
      setCuriositaVisible(false);
      setTimeout(() => {
        curiositaIdxRef.current = (curiositaIdxRef.current + 1) % shuffledCuriosita.current.length;
        setCuriositaText(shuffledCuriosita.current[curiositaIdxRef.current]);
        setCuriositaVisible(true);
      }, 400);
    }, 8000);
  };

  useEffect(() => {
    // Carica foto utente per miniatura
    const photo = localStorage.getItem('userPhoto');
    if (photo) setUserPhoto(photo);
    return () => {
      if (progressInterval.current) clearInterval(progressInterval.current);
      if (curiositaInterval.current) clearInterval(curiositaInterval.current);
      if (consiglioInterval.current) clearInterval(consiglioInterval.current);
    };
  }, []);

  // Build a fetchable URL from a try-on result URL. Route remote URLs through
  // /api/proxy-image so the response is same-origin and navigator.share /
  // download links work on mobile Safari (which ignores the download
  // attribute on cross-origin hrefs and opens the image in a new tab).
  const buildImageFetchUrl = (url) => {
    if (!url) return null;
    if (url.startsWith('data:')) return url;
    const base = process.env.NODE_ENV === 'production' ? '/api' : 'http://localhost:8080/api';
    return `${base}/proxy-image?url=${encodeURIComponent(url)}`;
  };

  const fetchAsFile = async (url) => {
    const fetchUrl = buildImageFetchUrl(url);
    if (!fetchUrl) return null;
    const res = await fetch(fetchUrl);
    if (!res.ok) throw new Error(`Fetch failed: ${res.status}`);
    const blob = await res.blob();
    const type = blob.type || 'image/jpeg';
    const ext = type.includes('png') ? 'png' : 'jpg';
    return new File([blob], `tryon.${ext}`, { type });
  };

  // Pre-fetch tryOnImage into a File so native share opens instantly on tap.
  // iOS Safari needs the File ready synchronously in the gesture handler.
  useEffect(() => {
    if (!tryOnImage) { setTryOnImageFile(null); return; }
    let cancelled = false;
    (async () => {
      try {
        const file = await fetchAsFile(tryOnImage);
        if (!cancelled && file) setTryOnImageFile(file);
      } catch (e) {
        console.warn('Pre-fetch tryOn image for share failed:', e?.message);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tryOnImage]);

  // Ensures we have a File ready. Uses the pre-fetched one if present,
  // otherwise fetches now and caches for subsequent taps.
  const ensureFile = async () => {
    if (tryOnImageFile) return tryOnImageFile;
    if (!tryOnImage) return null;
    try {
      const file = await fetchAsFile(tryOnImage);
      if (file) setTryOnImageFile(file);
      return file;
    } catch (e) {
      console.warn('On-demand fetch failed:', e?.message);
      return null;
    }
  };

  // Start consigli slider when armocromia becomes available
  useEffect(() => {
    if (!armocromia?.season) return;
    const consigliSet = product?.genere === 'uomo'
      ? CONSIGLI_PER_STAGIONE_UOMO
      : CONSIGLI_PER_STAGIONE_DONNA;
    const pool = consigliSet[armocromia.season] || [];
    if (pool.length === 0) return;

    // Prepend AI-generated tips if present
    const extras = [];
    if (armocromia.tips) {
      extras.push({ icon: '💡', title: 'Consiglio dell\'AI', text: armocromia.tips });
    }

    shuffledConsigli.current = [...extras, ...shuffleArray(pool)];
    setConsiglioIdx(0);
    setConsiglioVisible(true);
    setConsiglioDirection('next');

    if (consiglioInterval.current) clearInterval(consiglioInterval.current);
    let idx = 0;
    consiglioInterval.current = setInterval(() => {
      setConsiglioDirection('next');
      setConsiglioVisible(false);
      setTimeout(() => {
        idx = (idx + 1) % shuffledConsigli.current.length;
        setConsiglioIdx(idx);
        setConsiglioVisible(true);
      }, 280);
    }, 9000);

    return () => {
      if (consiglioInterval.current) clearInterval(consiglioInterval.current);
    };
  }, [armocromia?.season, armocromia?.tips, product?.genere]);

  const goToConsiglio = (direction) => {
    if (consiglioInterval.current) clearInterval(consiglioInterval.current);
    setConsiglioDirection(direction > 0 ? 'next' : 'prev');
    setConsiglioVisible(false);
    setTimeout(() => {
      const len = shuffledConsigli.current.length;
      if (len === 0) return;
      const newIdx = (consiglioIdx + direction + len) % len;
      setConsiglioIdx(newIdx);
      setConsiglioVisible(true);
    }, 250);
    // Restart auto-rotate
    let idx = (consiglioIdx + direction + shuffledConsigli.current.length) % shuffledConsigli.current.length;
    consiglioInterval.current = setInterval(() => {
      setConsiglioDirection('next');
      setConsiglioVisible(false);
      setTimeout(() => {
        idx = (idx + 1) % shuffledConsigli.current.length;
        setConsiglioIdx(idx);
        setConsiglioVisible(true);
      }, 280);
    }, 9000);
  };

  useEffect(() => {
    if (initialized.current && lastLoadedProduct.current === productIdParam) return;
    initialized.current = true;
    lastLoadedProduct.current = productIdParam;

    // Reset state when navigating to a new product (e.g. "try with same photo" from a recommendation)
    setArmocromia(null);
    setTryOnImage(null);
    setRecommendations([]);
    setError(null);
    setLoading(true);
    setContactSent(false);
    setTryOnPending(false);

    loadAndProcess();
    return () => {
      // StrictMode remount: allow loadAndProcess to run again on re-mount
      initialized.current = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productIdParam]);

  const loadAndProcess = async () => {
    startProgressBar();
    try {
      // 1. Load product — localStorage first, then API fallback
      let productData = null;
      const savedProduct = localStorage.getItem('currentProduct');
      if (savedProduct) {
        try {
          productData = JSON.parse(savedProduct);
        } catch (e) {
          console.error('Failed to parse currentProduct from localStorage');
        }
      }

      if (!productData && productIdParam) {
        console.log('Product not in localStorage, fetching from API:', productIdParam);
        try {
          productData = await getProductById(productIdParam);
        } catch (e) {
          console.error('Failed to fetch product from API:', e);
        }
      }

      if (!productData) {
        setError('Nessun prodotto selezionato. Torna alla scansione del prodotto.');
        stopProgressBar();
        setLoading(false);
        return;
      }

      setProduct(productData);

      // Check if already saved
      const savedItems = JSON.parse(localStorage.getItem('savedItems') || '[]');
      setIsSaved(savedItems.some(item => item.id === productData.id));

      // 2. Load photo — prefer Firebase URL, fallback to base64 (server handles upload)
      let savedPhoto = localStorage.getItem('userPhoto');
      let photoUrlForApi = null;

      try {
        const photoMeta = JSON.parse(localStorage.getItem('userPhotoMetadata') || 'null');
        if (photoMeta && photoMeta.url && Date.now() < photoMeta.expiresAt) {
          photoUrlForApi = photoMeta.url;
          console.log('Using Firebase Storage URL for try-on');
        }
      } catch (e) {
        console.error('Failed to parse photo metadata:', e);
      }

      // photoForApi = Firebase URL if available, otherwise base64
      // Server now handles base64→URL upload via admin SDK
      const photoForApi = photoUrlForApi || savedPhoto;

      if (!photoForApi) {
        setError('Nessuna foto trovata. Torna alla schermata di cattura foto.');
        setLoading(false);
        return;
      }

      // Read email for server-side sending
      let userEmail = null;
      try {
        const contactData = JSON.parse(localStorage.getItem('userContact') || 'null');
        if (contactData?.type === 'email' && contactData.value) {
          userEmail = contactData.value;
        }
      } catch (e) { /* ignore */ }

      // Check if try-on was pre-started from PhotoCapture (saves ~20-30s of perceived wait)
      const preStartedSessionId = localStorage.getItem('tryOnSessionId');
      const cached = preStartedSessionId ? getCachedTryOn(preStartedSessionId) : null;

      // Reuse the pre-started sessionId if available; otherwise generate a new one
      const sessionId = (cached && preStartedSessionId)
        || (userEmail ? `${Date.now()}_${Math.random().toString(36).substr(2, 9)}` : null);

      // 3. Launch armocromia + try-on in PARALLEL, but show armocromia as soon as it arrives
      setLoadingMessage(cached ? 'Recupero prova virtuale...' : 'Analisi del tono della pelle...');

      let armocromiaData = null;
      let emailSentByServer = false;

      // Armocromia promise — updates UI immediately when resolved
      const armocromiaPromise = analyzeArmocromia(savedPhoto || photoForApi, userEmail, sessionId, productData?.genere)
        .then(result => {
          if (result) {
            armocromiaData = result;
            setArmocromia(result);
            if (result.emailSent) emailSentByServer = true;
          }
          return result;
        })
        .catch(err => {
          console.error('Armocromia analysis failed:', err);
          return null;
        });

      // Try-on promise — reuse pre-started request if available
      let tryOnPromise;
      if (cached && cached.promise) {
        console.log(`⚡ Reusing pre-started try-on (started ${Math.round((Date.now() - cached.startedAt) / 1000)}s ago)`);
        tryOnPromise = cached.promise.finally(() => clearCachedTryOn(preStartedSessionId));
      } else {
        tryOnPromise = doTryOn(photoForApi, productData, userEmail, sessionId);
      }

      // Read contact info for graceful degradation message
      try {
        const contactData = JSON.parse(localStorage.getItem('userContact') || 'null');
        if (contactData?.value) setUserContactInfo(contactData);
      } catch (e) { /* ignore */ }

      // Wait for BOTH to finish (armocromia shows as soon as it resolves via .then above)
      const [, tryOnResult] = await Promise.allSettled([armocromiaPromise, tryOnPromise]);

      // Handle try-on result
      let finalImage = savedPhoto; // fallback to user photo
      let tryOnFailed = false;
      let poseFailed = false;
      if (tryOnResult.status === 'fulfilled' && tryOnResult.value) {
        const val = tryOnResult.value;
        if (typeof val === 'object' && val.resultUrl) {
          finalImage = val.resultUrl;
          if (val.emailSent) emailSentByServer = true;
        } else {
          finalImage = val;
        }
      } else {
        console.warn('Try-on failed/timeout:', tryOnResult.reason);
        tryOnFailed = true;
        // Check if the error is a pose detection failure (FASHN specific)
        const reasonErr = tryOnResult.reason;
        if (reasonErr?.errorCode === 'pose_detection_failed' || /pose|body|detect/i.test(reasonErr?.message || '')) {
          poseFailed = true;
          setError('La foto non è adatta: scatta una nuova foto inquadrando almeno il busto, non solo il viso.');
        } else {
          // Generic timeout / error → friendly pending notice
          setTryOnPending(true);
        }
      }

      setTryOnImage(finalImage);
      if (tryOnFailed && !poseFailed) {
        setError(null);
      }

      // Show email confirmation if server sent it
      if (emailSentByServer) setContactSent(true);

      stopProgressBar();
      setLoading(false);

      // 4. Fire-and-forget: tracking + recommendations
      trackResults(productData, finalImage, armocromiaData);
      loadRecommendations(productData, armocromiaData);

    } catch (err) {
      console.error('Fatal error in loadAndProcess:', err);
      setError('Si è verificato un errore. Riprova.');
      stopProgressBar();
      setLoading(false);
    }
  };

  const doTryOn = async (photo, productData, email, sessionId) => {
    // Vercel caps the function at 120s. Keep the client timeout slightly higher
    // (175s) to leave room for FASHN + upload + network jitter without spuriously
    // falling into the email-pending fallback when the server actually succeeded.
    const started = Date.now();
    const timeoutPromise = new Promise((_, reject) =>
      setTimeout(() => reject(new Error('timeout')), 175000)
    );
    try {
      return await Promise.race([
        performVirtualTryOn(photo, productData, email, sessionId),
        timeoutPromise
      ]);
    } catch (err) {
      console.warn(`[tryon] doTryOn rejected after ${Math.round((Date.now() - started) / 1000)}s: ${err?.message || err}`);
      throw err;
    }
  };

  const analyzeArmocromia = async (photo, email, sessionId, gender) => {
    try {
      const captchaToken = await getCaptchaToken();
      const body = { photo, captchaToken };
      if (email) body.email = email;
      if (sessionId) body.sessionId = sessionId;
      if (gender === 'uomo' || gender === 'donna') body.gender = gender;
      const result = await apiCall('/analyze-armocromia', {
        method: 'POST',
        body: JSON.stringify(body)
      });
      return result;
    } catch (err) {
      console.error('Armocromia analysis failed:', err);
      return null;
    }
  };

  const trackResults = async (productData, finalImage, armocromiaData) => {
    // Track on Firebase (fire-and-forget)
    const contactHash = localStorage.getItem('contactHash');
    if (contactHash) {
      trackInteraction(contactHash, {
        productId: productData.id,
        productName: productData.name,
        type: 'tryon'
      }).catch(() => {});
    }
    trackTryOn(productData.id, productData.name, contactHash).catch(() => {});
    // Email is now sent server-side via the API endpoints
  };

  const loadRecommendations = async (currentProduct, armocromiaData) => {
    try {
      const allProducts = await getAllProducts();
      if (!allProducts || allProducts.length === 0) return;

      let filtered = allProducts.filter(p => p.id !== currentProduct?.id);

      // Gender filter — hard gate if we have enough same-gender items
      if (currentProduct?.genere === 'uomo' || currentProduct?.genere === 'donna') {
        const sameGender = filtered.filter(p => p.genere === currentProduct.genere);
        if (sameGender.length >= 4) filtered = sameGender;
      }

      const norm = (s) => String(s || '').toLowerCase().trim();
      const currentCat = norm(currentProduct?.category || currentProduct?.categoria);

      const scored = filtered.map(p => {
        // Primary: armocromia alignment (palette match > season fit > avoid)
        let armocromiaScore = scoreAgainstArmocromia(p.colore_principale, armocromiaData);
        // Secondary: same-category coherence
        const catScore = (currentCat && norm(p.category || p.categoria) === currentCat) ? 1 : 0;
        // Tie-break randomness so the set isn't identical across reloads
        const jitter = Math.random() * 0.5;
        const total = armocromiaScore + catScore + jitter;
        return { p, score: total, armocromiaScore };
      });

      scored.sort((a, b) => b.score - a.score);
      // Attach match strength so the card can render a badge for strong fits.
      // Threshold 5 requires at least a 2-season-specific color match or a
      // direct palette hit from the AI — excludes generic colors like "bianco"
      // which technically fit every season but don't mean much.
      setRecommendations(
        scored.slice(0, 6).map(s => ({
          ...s.p,
          _armocromiaMatch: s.armocromiaScore >= 5
        }))
      );
    } catch (err) {
      console.error('Error loading recommendations:', err);
    }
  };

  const toggleSave = () => {
    if (!product) return;
    const savedItems = JSON.parse(localStorage.getItem('savedItems') || '[]');
    if (isSaved) {
      const updated = savedItems.filter(item => item.id !== product.id);
      localStorage.setItem('savedItems', JSON.stringify(updated));
      setIsSaved(false);
    } else {
      savedItems.push({
        ...product,
        tryOnImage: tryOnImage,
        savedAt: new Date().toISOString()
      });
      localStorage.setItem('savedItems', JSON.stringify(savedItems));
      setIsSaved(true);
    }
  };

  // Open the native share sheet with the generated image as a File.
  // Best path is when the File is already pre-fetched (instant, keeps iOS
  // user activation). If not, ensureFile() fetches via proxy (same-origin)
  // before calling navigator.share.
  const nativeShareImage = async (text) => {
    if (!navigator.share) return false;
    const file = await ensureFile();
    if (!file) return false;
    if (navigator.canShare && !navigator.canShare({ files: [file] })) return false;
    try {
      await navigator.share({
        title: `Prova virtuale - ${product?.name || 'Prodotto'}`,
        text,
        files: [file]
      });
      return true;
    } catch (err) {
      if (err && err.name === 'AbortError') return true; // user cancelled
      // iOS Safari may throw NotAllowedError if transient activation expired
      // while we awaited the fetch. Signal fallback so caller can download.
      if (err && err.name === 'NotAllowedError') {
        console.warn('Share not allowed (activation expired) — falling back');
        return false;
      }
      console.warn('Native share failed:', err?.message);
      return false;
    }
  };

  // Download using a blob: URL so the download attribute works on mobile
  // Safari (cross-origin hrefs open in a new tab instead of saving).
  // Returns true if the download link fired, false if we couldn't get a file.
  const downloadImage = async () => {
    const file = await ensureFile();
    if (!file) return false;
    const objectUrl = URL.createObjectURL(file);
    const link = document.createElement('a');
    link.href = objectUrl;
    link.download = `tryon_${product?.name || 'product'}.jpg`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    setTimeout(() => URL.revokeObjectURL(objectUrl), 1000);
    return true;
  };

  const handleShare = async () => {
    const shown = await nativeShareImage('Guarda come mi sta questo capo!');
    if (!shown) await downloadImage();
  };

  const handleMenuAction = async (action) => {
    setShareError(null);
    // Track whether we need to show a prep spinner. We never close the drawer
    // up-front — only in the finally block on success — so if any path fails
    // we can surface the error inline instead of a silent no-op.
    const needsPrep = !tryOnImageFile;
    if (needsPrep) setSharePendingAction(action);

    let anyPathSucceeded = false;
    try {
      if (action === 'save') {
        const shown = await nativeShareImage('Salva la tua prova virtuale');
        if (shown) anyPathSucceeded = true;
        else {
          const ok = await downloadImage();
          anyPathSucceeded = ok;
        }
      } else if (action === 'whatsapp') {
        const shown = await nativeShareImage('Guarda il mio try-on con ProvaMi!');
        if (shown) anyPathSucceeded = true;
        else {
          // wa.me link always opens; counts as success
          const text = encodeURIComponent('Guarda il mio try-on con ProvaMi! https://provami.app');
          window.open(`https://wa.me/?text=${text}`, '_blank');
          anyPathSucceeded = true;
        }
      } else if (action === 'share') {
        const shown = await nativeShareImage('Guarda come mi sta questo capo!');
        if (shown) anyPathSucceeded = true;
        else {
          const ok = await downloadImage();
          anyPathSucceeded = ok;
        }
      }
    } catch (err) {
      console.error('Share action error:', err);
    } finally {
      setSharePendingAction(null);
      if (anyPathSucceeded) {
        setShareMenuOpen(false);
      } else {
        // Drawer stays open so user sees the error and can retry
        setShareError('Impossibile preparare l\'immagine. Controlla la connessione e riprova.');
      }
    }
  };

  // No product — show error with back button
  if (!loading && !product) {
    return (
      <Box
        sx={{
          height: '100dvh',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          px: 3,
          gap: 3,
          overflow: 'hidden'
        }}
      >
        <Typography sx={{ fontSize: '20px', fontWeight: 700, color: 'white', textAlign: 'center' }}>
          {error || 'Nessun prodotto selezionato'}
        </Typography>
        <Button
          variant="contained"
          startIcon={<QrCodeScannerIcon />}
          onClick={() => navigate('/scan')}
          sx={{
            background: 'rgba(255,255,255,0.95)',
            color: '#667eea',
            fontWeight: 700,
            borderRadius: '14px',
            textTransform: 'none',
            px: 4, py: 1.5
          }}
        >
          Scansiona un prodotto
        </Button>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        height: '100dvh',
        background: loading
          ? 'linear-gradient(160deg, #1a1a2e 0%, #16213e 40%, #0f3460 100%)'
          : '#f5f5f5',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden'
      }}
    >
      {/* Loading state */}
      {loading ? (
        <Box
          sx={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden'
          }}
        >
          {/* Fixed top: progress bar + status */}
          <Box
            sx={{
              px: 3,
              pt: 4,
              pb: 2,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 2,
              flexShrink: 0
            }}
          >
            <Typography
              sx={{
                fontSize: { xs: '20px', sm: '24px' },
                fontWeight: 800,
                color: 'white',
                textAlign: 'center'
              }}
            >
              {armocromia ? 'Prova virtuale in corso...' : 'Stiamo elaborando il tuo look'}
            </Typography>

            {/* Progress bar */}
            <Box sx={{ width: '100%', maxWidth: '340px', display: 'flex', flexDirection: 'column', gap: 1.25 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <LinearProgress
                  variant="determinate"
                  value={progress}
                  sx={{
                    flex: 1,
                    height: 6,
                    borderRadius: 3,
                    backgroundColor: 'rgba(255,255,255,0.12)',
                    '& .MuiLinearProgress-bar': {
                      borderRadius: 3,
                      background: 'linear-gradient(90deg, #9ba8ff, #c8a7ff)',
                      transition: 'transform 0.3s ease'
                    }
                  }}
                />
                <Typography sx={{ fontSize: '13px', color: 'rgba(255,255,255,0.7)', fontWeight: 700, minWidth: '36px', textAlign: 'right' }}>
                  {Math.round(progress)}%
                </Typography>
              </Box>

              {/* Single rolling step — one line, cross-fades on change */}
              <Box sx={{ minHeight: '22px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                <Box
                  sx={{
                    width: 6,
                    height: 6,
                    borderRadius: '50%',
                    backgroundColor: '#c8a7ff',
                    flexShrink: 0,
                    animation: 'pulse 1.5s ease-in-out infinite',
                    '@keyframes pulse': {
                      '0%, 100%': { opacity: 1, transform: 'scale(1)' },
                      '50%': { opacity: 0.4, transform: 'scale(0.8)' }
                    }
                  }}
                />
                <Fade in key={loadingMessage} timeout={400}>
                  <Typography
                    sx={{
                      fontSize: '13px',
                      color: 'rgba(255,255,255,0.7)',
                      fontWeight: 500,
                      textAlign: 'center'
                    }}
                  >
                    {loadingMessage}
                  </Typography>
                </Fade>
              </Box>
            </Box>
          </Box>

          {/* Scrollable armocromia results (shown while try-on loads) */}
          {armocromia ? (
            <Box
              sx={{
                flex: 1,
                minHeight: 0,
                overflowY: 'auto',
                overflowX: 'hidden',
                px: 2.5,
                pb: 3,
                '&::-webkit-scrollbar': { display: 'none' }
              }}
            >
              <Box
                sx={{
                  backgroundColor: 'rgba(255,255,255,0.95)',
                  borderRadius: '20px',
                  p: 3,
                  textAlign: 'center',
                  animation: 'fadeInUp 0.6s ease-out',
                  '@keyframes fadeInUp': {
                    from: { opacity: 0, transform: 'translateY(20px)' },
                    to: { opacity: 1, transform: 'translateY(0)' }
                  }
                }}
              >
                <Typography sx={{ fontSize: '12px', color: '#888', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '1px', mb: 0.5 }}>
                  Intanto scopri la tua armocromia
                </Typography>
                <Typography sx={{ fontSize: '14px', color: '#888', fontWeight: 500, mb: 1 }}>
                  La tua stagione è
                </Typography>
                <Typography
                  sx={{
                    fontSize: { xs: '28px', sm: '32px' },
                    fontWeight: 800,
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    mb: 0.5
                  }}
                >
                  {armocromia.season}
                </Typography>
                {armocromia.subSeason && (
                  <Typography sx={{ fontSize: '15px', color: '#764ba2', fontWeight: 600, mb: 1 }}>
                    {armocromia.subSeason}
                  </Typography>
                )}

                {/* Observed traits */}
                {(armocromia.skinTone || armocromia.hairColor || armocromia.eyeColor) && (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: 0.8, mb: 1.5 }}>
                    {armocromia.skinTone && (
                      <Box sx={{ fontSize: '11px', color: '#667eea', fontWeight: 600, backgroundColor: 'rgba(102,126,234,0.08)', borderRadius: '8px', px: 1.2, py: 0.4 }}>
                        Pelle: {armocromia.skinTone}
                      </Box>
                    )}
                    {armocromia.hairColor && (
                      <Box sx={{ fontSize: '11px', color: '#667eea', fontWeight: 600, backgroundColor: 'rgba(102,126,234,0.08)', borderRadius: '8px', px: 1.2, py: 0.4 }}>
                        Capelli: {armocromia.hairColor}
                      </Box>
                    )}
                    {armocromia.eyeColor && (
                      <Box sx={{ fontSize: '11px', color: '#667eea', fontWeight: 600, backgroundColor: 'rgba(102,126,234,0.08)', borderRadius: '8px', px: 1.2, py: 0.4 }}>
                        Occhi: {armocromia.eyeColor}
                      </Box>
                    )}
                  </Box>
                )}

                {armocromia.undertone && (
                  <Typography sx={{ fontSize: '13px', color: '#667eea', fontWeight: 600, mb: 1.5 }}>
                    Sottotono: {armocromia.undertone === 'warm' ? 'Caldo' : armocromia.undertone === 'cool' ? 'Freddo' : 'Neutro'}
                    {armocromia.contrast && ` · Contrasto: ${armocromia.contrast === 'low' ? 'Basso' : armocromia.contrast === 'medium' ? 'Medio' : 'Alto'}`}
                  </Typography>
                )}

                <Typography sx={{ fontSize: '13px', color: '#555', lineHeight: 1.7, mb: 2, maxWidth: '360px', mx: 'auto', textAlign: 'left' }}>
                  {armocromia.description}
                </Typography>
              </Box>

              {/* Consigli slider during loading — keeps user engaged while try-on runs */}
              {shuffledConsigli.current.length > 0 && (() => {
                const current = shuffledConsigli.current[consiglioIdx] || {};
                return (
                  <Box
                    sx={{
                      mt: 2,
                      backgroundColor: 'rgba(255,255,255,0.96)',
                      borderRadius: '20px',
                      p: 2.5,
                      animation: 'fadeInUp 0.6s ease-out',
                      overflow: 'hidden'
                    }}
                  >
                    <Typography sx={{ fontSize: '10px', fontWeight: 700, color: '#667eea', textTransform: 'uppercase', letterSpacing: '1.5px', mb: 1.5, textAlign: 'left' }}>
                      Consigli per te
                    </Typography>
                    <Box sx={{ minHeight: '100px', overflow: 'hidden' }}>
                      <Box
                        key={consiglioIdx}
                        sx={{
                          opacity: consiglioVisible ? 1 : 0,
                          transform: consiglioVisible ? 'translateX(0)' : `translateX(${consiglioDirection === 'next' ? '-30px' : '30px'})`,
                          transition: 'opacity 0.35s ease, transform 0.45s cubic-bezier(0.2, 0.9, 0.3, 1.1)',
                          display: 'flex',
                          gap: 1.5,
                          textAlign: 'left'
                        }}
                      >
                        <Box sx={{ width: 40, height: 40, borderRadius: '12px', background: 'linear-gradient(135deg, #667eea, #764ba2)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '20px', flexShrink: 0 }}>
                          {current.icon}
                        </Box>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography sx={{ fontSize: '14px', fontWeight: 800, color: '#222', mb: 0.5, lineHeight: 1.2 }}>
                            {current.title}
                          </Typography>
                          <Typography sx={{ fontSize: '12px', color: '#555', lineHeight: 1.55 }}>
                            {current.text}
                          </Typography>
                        </Box>
                      </Box>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 0.5, mt: 1.5 }}>
                      <IconButton onClick={() => goToConsiglio(-1)} size="small" sx={{ color: '#999', background: 'rgba(102,126,234,0.08)', '&:hover': { color: '#667eea' } }}>
                        <ChevronLeftIcon fontSize="small" />
                      </IconButton>
                      <Box sx={{ display: 'flex', gap: 0.5, mx: 1.5 }}>
                        {Array.from({ length: Math.min(shuffledConsigli.current.length, 10) }).map((_, i) => (
                          <Box
                            key={i}
                            sx={{
                              width: i === consiglioIdx % 10 ? 14 : 5,
                              height: 5,
                              borderRadius: '3px',
                              backgroundColor: i === consiglioIdx % 10 ? '#667eea' : 'rgba(102,126,234,0.2)',
                              transition: 'width 0.35s ease, background-color 0.35s ease'
                            }}
                          />
                        ))}
                      </Box>
                      <IconButton onClick={() => goToConsiglio(1)} size="small" sx={{ color: '#999', background: 'rgba(102,126,234,0.08)', '&:hover': { color: '#667eea' } }}>
                        <ChevronRightIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  </Box>
                );
              })()}
            </Box>
          ) : (
            /* No armocromia yet — photo thumbnail + scan animation + curiosità */
            <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', px: 3, pt: 2, gap: 2.5, overflowY: 'auto', overflowX: 'hidden' }}>

              {/* Miniatura foto utente con animazione scansione */}
              {userPhoto && (
                <Box sx={{ position: 'relative', width: 120, height: 120 }}>
                  {/* Foto circolare */}
                  <Box
                    sx={{
                      width: 120,
                      height: 120,
                      borderRadius: '50%',
                      overflow: 'hidden',
                      border: '3px solid rgba(255,255,255,0.3)',
                      boxShadow: '0 0 30px rgba(102,126,234,0.3)'
                    }}
                  >
                    <img src={userPhoto} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                  </Box>
                  {/* Linea di scansione animata */}
                  <Box
                    sx={{
                      position: 'absolute',
                      top: 0,
                      left: '10%',
                      width: '80%',
                      height: '2px',
                      background: 'linear-gradient(90deg, transparent, #667eea, #764ba2, transparent)',
                      borderRadius: '2px',
                      boxShadow: '0 0 8px rgba(102,126,234,0.6)',
                      animation: 'scanLine 2.5s ease-in-out infinite',
                      '@keyframes scanLine': {
                        '0%': { top: '5%', opacity: 0 },
                        '10%': { opacity: 1 },
                        '90%': { opacity: 1 },
                        '100%': { top: '95%', opacity: 0 }
                      }
                    }}
                  />
                  {/* Glow pulsante attorno alla foto */}
                  <Box
                    sx={{
                      position: 'absolute',
                      top: -4,
                      left: -4,
                      right: -4,
                      bottom: -4,
                      borderRadius: '50%',
                      border: '2px solid rgba(102,126,234,0.4)',
                      animation: 'glowPulse 2s ease-in-out infinite',
                      '@keyframes glowPulse': {
                        '0%, 100%': { boxShadow: '0 0 10px rgba(102,126,234,0.2)', transform: 'scale(1)' },
                        '50%': { boxShadow: '0 0 25px rgba(102,126,234,0.5)', transform: 'scale(1.03)' }
                      }
                    }}
                  />
                </Box>
              )}

              {/* Curiosità con frecce navigazione */}
              {curiositaText && (
                <Box sx={{ maxWidth: '340px', width: '100%' }}>
                  <Box
                    sx={{
                      backgroundColor: 'rgba(255,255,255,0.1)',
                      borderRadius: '16px',
                      p: 2.5,
                      minHeight: '70px',
                      display: 'flex',
                      alignItems: 'center'
                    }}
                  >
                    <Fade in={curiositaVisible} timeout={400}>
                      <Box sx={{ width: '100%' }}>
                        <Typography sx={{ fontSize: '11px', fontWeight: 700, color: 'rgba(255,255,255,0.5)', textTransform: 'uppercase', letterSpacing: '1px', mb: 0.5 }}>
                          Lo sapevi?
                        </Typography>
                        <Typography sx={{ fontSize: '13px', color: 'rgba(255,255,255,0.85)', lineHeight: 1.6 }}>
                          {curiositaText}
                        </Typography>
                      </Box>
                    </Fade>
                  </Box>
                  {/* Frecce navigazione */}
                  <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, mt: 1 }}>
                    <IconButton
                      onClick={() => goToCuriosita(-1)}
                      sx={{ color: 'rgba(255,255,255,0.5)', p: 0.5, '&:hover': { color: 'rgba(255,255,255,0.9)' } }}
                    >
                      <ChevronLeftIcon sx={{ fontSize: 28 }} />
                    </IconButton>
                    <IconButton
                      onClick={() => goToCuriosita(1)}
                      sx={{ color: 'rgba(255,255,255,0.5)', p: 0.5, '&:hover': { color: 'rgba(255,255,255,0.9)' } }}
                    >
                      <ChevronRightIcon sx={{ fontSize: 28 }} />
                    </IconButton>
                  </Box>
                </Box>
              )}
            </Box>
          )}
        </Box>
      ) : (
        <>
          {/* Header */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: '16px 24px',
              backgroundColor: '#ffffff',
              boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)'
            }}
          >
            <IconButton onClick={() => navigate('/')}>
              <ArrowBackIcon />
            </IconButton>
            <Typography sx={{ fontSize: '18px', fontWeight: 700, color: '#333' }}>
              I tuoi risultati
            </Typography>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <IconButton onClick={toggleSave}>
                {isSaved ? (
                  <FavoriteIcon sx={{ color: '#f44336' }} />
                ) : (
                  <FavoriteBorderIcon sx={{ color: '#667eea' }} />
                )}
              </IconButton>
              <IconButton onClick={handleShare}>
                <ShareIcon sx={{ color: '#667eea' }} />
              </IconButton>
            </Box>
          </Box>

          {/* Error Alert */}
          {error && (
            <Box sx={{ px: 2, pt: 2 }}>
              <Alert severity="warning" onClose={() => setError(null)}>
                {error}
              </Alert>
            </Box>
          )}

          {/* Try-on pending notice (sent when Segmind took too long) */}
          {tryOnPending && (
            <Box sx={{ px: 2, pt: 2 }}>
              <Box
                sx={{
                  background: 'linear-gradient(135deg, rgba(102,126,234,0.08), rgba(118,75,162,0.08))',
                  border: '1px solid rgba(102,126,234,0.2)',
                  borderRadius: '16px',
                  p: 2.5,
                  display: 'flex',
                  gap: 2,
                  alignItems: 'flex-start'
                }}
              >
                <Box
                  sx={{
                    width: 40,
                    height: 40,
                    flexShrink: 0,
                    borderRadius: '50%',
                    background: 'linear-gradient(135deg, #667eea, #764ba2)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '20px'
                  }}
                >
                  {userContactInfo?.type === 'phone' ? '📱' : '✉️'}
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Typography sx={{ fontSize: '14px', fontWeight: 700, color: '#333', mb: 0.5 }}>
                    Ti invieremo la prova virtuale
                  </Typography>
                  <Typography sx={{ fontSize: '13px', color: '#666', lineHeight: 1.5 }}>
                    {userContactInfo?.type === 'phone'
                      ? <>Stiamo completando la tua prova virtuale. Riceverai il risultato via <strong>SMS al {userContactInfo.value}</strong> appena pronto.</>
                      : userContactInfo?.type === 'email'
                      ? <>Stiamo completando la tua prova virtuale. Riceverai il risultato via <strong>email a {userContactInfo.value}</strong> appena pronto.</>
                      : <>Stiamo completando la tua prova virtuale. Riceverai il risultato appena pronto.</>
                    }
                  </Typography>
                  <Typography sx={{ fontSize: '12px', color: '#888', mt: 1, fontStyle: 'italic' }}>
                    Nel frattempo, scopri la tua armocromia e i capi consigliati ↓
                  </Typography>
                </Box>
              </Box>
            </Box>
          )}

          {/* Contact sent confirmation */}
          {contactSent && !tryOnPending && (
            <Box sx={{ px: 2, pt: 2 }}>
              <Alert
                severity="success"
                icon={<CheckCircleIcon />}
                sx={{ borderRadius: '12px' }}
              >
                Risultati inviati alla tua email!
              </Alert>
            </Box>
          )}

          {/* Scrollable content */}
          <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto', overflowX: 'hidden', pb: 4 }}>
            {/* Section 1: Try-On Result (hidden when pending) */}
            {!tryOnPending && (
            <Box sx={{ p: { xs: 2, sm: 3 } }}>
              <Typography
                sx={{
                  fontSize: '13px',
                  fontWeight: 600,
                  color: '#667eea',
                  textTransform: 'uppercase',
                  letterSpacing: '1px',
                  mb: 1.5
                }}
              >
                Risultato Try-On
              </Typography>
              <Card
                sx={{
                  borderRadius: '20px',
                  overflow: 'hidden',
                  boxShadow: '0 8px 30px rgba(0, 0, 0, 0.1)'
                }}
              >
                <Box
                  onClick={() => tryOnImage && setShareMenuOpen(true)}
                  sx={{
                    position: 'relative',
                    display: 'block',
                    fontSize: 0,
                    cursor: tryOnImage ? 'pointer' : 'default',
                    '&:active': tryOnImage ? { opacity: 0.92 } : {}
                  }}
                >
                  <img
                    src={tryOnImage}
                    alt="Try-on result"
                    style={{
                      display: 'block',
                      width: '100%',
                      height: 'auto'
                    }}
                  />
                  {tryOnImage && (
                    <Box
                      sx={{
                        position: 'absolute',
                        bottom: 12,
                        right: 12,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.7,
                        px: 1.2,
                        py: 0.6,
                        borderRadius: '999px',
                        background: 'rgba(0,0,0,0.55)',
                        backdropFilter: 'blur(8px)',
                        WebkitBackdropFilter: 'blur(8px)',
                        border: '1px solid rgba(255,255,255,0.15)',
                        pointerEvents: 'none'
                      }}
                    >
                      <IosShareIcon sx={{ color: 'white', fontSize: 14 }} />
                      <Typography sx={{ color: 'white', fontSize: '11px', fontWeight: 600, letterSpacing: '0.3px' }}>
                        Tocca per condividere
                      </Typography>
                    </Box>
                  )}
                </Box>
                <CardContent sx={{ p: 2.5 }}>
                  <Typography sx={{ fontSize: '18px', fontWeight: 700, color: '#333' }}>
                    {product.name}
                  </Typography>
                  {product.brand && (
                    <Typography sx={{ fontSize: '14px', color: '#888', mt: 0.5 }}>
                      {product.brand}
                    </Typography>
                  )}
                  <Typography sx={{ fontSize: '16px', fontWeight: 600, color: '#667eea', mt: 0.5 }}>
                    €{product.price}
                  </Typography>
                </CardContent>
              </Card>
            </Box>
            )}

            {/* Section 2: Product Recommendations (armocromia + gender aware) */}
            {recommendations.length > 0 && (
              <Box sx={{ px: { xs: 2, sm: 3 }, pb: 2 }}>
                <Typography
                  sx={{
                    fontSize: '13px',
                    fontWeight: 600,
                    color: '#667eea',
                    textTransform: 'uppercase',
                    letterSpacing: '1px',
                    mb: 1.5
                  }}
                >
                  {armocromia ? 'Capi consigliati per la tua armocromia' : 'Capi consigliati per te'}
                </Typography>
                <Box
                  sx={{
                    display: 'flex',
                    gap: 2,
                    overflowX: 'auto',
                    pb: 1,
                    '&::-webkit-scrollbar': { display: 'none' }
                  }}
                >
                  {recommendations.map((rec) => (
                    <Card
                      key={rec.id}
                      onClick={() => setRecDialogProduct(rec)}
                      sx={{
                        minWidth: '160px',
                        maxWidth: '160px',
                        borderRadius: '16px',
                        boxShadow: '0 4px 15px rgba(0, 0, 0, 0.08)',
                        cursor: 'pointer',
                        flexShrink: 0,
                        transition: 'all 0.25s ease',
                        '&:hover': {
                          transform: 'translateY(-2px)',
                          boxShadow: '0 8px 25px rgba(0, 0, 0, 0.12)'
                        }
                      }}
                    >
                      {(rec.imageUrl || rec.immagine_url) && (
                        <Box
                          sx={{
                            position: 'relative',
                            height: '160px',
                            backgroundColor: '#f0f0f0',
                            overflow: 'hidden',
                            borderTopLeftRadius: '16px',
                            borderTopRightRadius: '16px'
                          }}
                        >
                          <img
                            src={getShopifyThumb(rec.imageUrl || rec.immagine_url, 400)}
                            alt={rec.name || rec.nome}
                            loading="lazy"
                            decoding="async"
                            style={{
                              width: '100%',
                              height: '100%',
                              objectFit: 'cover'
                            }}
                          />
                          {rec._armocromiaMatch && armocromia && (
                            <Box
                              sx={{
                                position: 'absolute',
                                top: 8,
                                left: 8,
                                display: 'flex',
                                alignItems: 'center',
                                gap: 0.5,
                                px: 0.9,
                                py: 0.3,
                                borderRadius: '999px',
                                background: 'linear-gradient(135deg, rgba(102,126,234,0.95), rgba(118,75,162,0.95))',
                                backdropFilter: 'blur(6px)',
                                boxShadow: '0 4px 12px rgba(102,126,234,0.35)'
                              }}
                            >
                              <Box sx={{ fontSize: '10px', lineHeight: 1 }}>✨</Box>
                              <Typography sx={{ color: 'white', fontSize: '9.5px', fontWeight: 800, letterSpacing: '0.3px', lineHeight: 1 }}>
                                PER LA TUA ARMOCROMIA
                              </Typography>
                            </Box>
                          )}
                          {(() => {
                            const tags = productSeasonTags(rec.colore_principale);
                            if (!tags.length || rec._armocromiaMatch) return null;
                            return (
                              <Box
                                sx={{
                                  position: 'absolute',
                                  bottom: 8,
                                  left: 8,
                                  px: 0.8,
                                  py: 0.25,
                                  borderRadius: '6px',
                                  background: 'rgba(0,0,0,0.55)',
                                  backdropFilter: 'blur(4px)'
                                }}
                              >
                                <Typography sx={{ color: 'white', fontSize: '9px', fontWeight: 700, letterSpacing: '0.3px', lineHeight: 1 }}>
                                  {tags.slice(0, 2).join(' · ')}
                                </Typography>
                              </Box>
                            );
                          })()}
                        </Box>
                      )}
                      <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                        <Typography
                          sx={{
                            fontSize: '13px',
                            fontWeight: 600,
                            color: '#333',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap'
                          }}
                        >
                          {rec.name || rec.nome}
                        </Typography>
                        <Typography
                          sx={{
                            fontSize: '14px',
                            fontWeight: 700,
                            color: '#667eea'
                          }}
                        >
                          €{typeof (rec.price ?? rec.prezzo) === 'number' ? (rec.price ?? rec.prezzo).toFixed(2) : (rec.price ?? rec.prezzo)}
                        </Typography>
                      </CardContent>
                    </Card>
                  ))}
                </Box>
              </Box>
            )}

            {/* Section 3: Armocromia Analysis */}
            {armocromia && (
              <Box sx={{ px: { xs: 2, sm: 3 }, pb: 2 }}>
                <Typography sx={{ fontSize: '13px', fontWeight: 600, color: '#667eea', textTransform: 'uppercase', letterSpacing: '1px', mb: 1.5 }}>
                  La tua Analisi Armocromia
                </Typography>
                <Card
                  sx={{
                    borderRadius: '24px',
                    overflow: 'hidden',
                    boxShadow: '0 12px 40px rgba(102, 126, 234, 0.15)',
                    position: 'relative',
                    animation: 'armoCardIn 0.6s cubic-bezier(0.34, 1.56, 0.64, 1)',
                    '@keyframes armoCardIn': {
                      '0%': { opacity: 0, transform: 'translateY(20px) scale(0.98)' },
                      '100%': { opacity: 1, transform: 'translateY(0) scale(1)' }
                    }
                  }}
                >
                  {/* Gradient header strip */}
                  <Box
                    sx={{
                      height: '6px',
                      background: 'linear-gradient(90deg, #667eea, #764ba2, #f093fb)'
                    }}
                  />
                  <CardContent sx={{ p: { xs: 2.5, sm: 3.5 }, textAlign: 'center', position: 'relative' }}>
                    {/* Decorative circle glow */}
                    <Box
                      sx={{
                        position: 'absolute',
                        top: '-80px',
                        left: '50%',
                        transform: 'translateX(-50%)',
                        width: '240px',
                        height: '240px',
                        borderRadius: '50%',
                        background: 'radial-gradient(circle, rgba(102,126,234,0.08) 0%, transparent 70%)',
                        pointerEvents: 'none'
                      }}
                    />

                    <Typography sx={{ fontSize: '12px', color: '#999', fontWeight: 500, mb: 0.5, letterSpacing: '0.5px', position: 'relative' }}>
                      LA TUA STAGIONE
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: { xs: '36px', sm: '44px' },
                        fontWeight: 900,
                        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 50%, #f093fb 100%)',
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent',
                        backgroundClip: 'text',
                        lineHeight: 1,
                        letterSpacing: '-1px',
                        position: 'relative',
                        animation: 'seasonIn 0.7s 0.2s ease-out backwards',
                        '@keyframes seasonIn': {
                          '0%': { opacity: 0, transform: 'translateY(15px)' },
                          '100%': { opacity: 1, transform: 'translateY(0)' }
                        }
                      }}
                    >
                      {armocromia.season}
                    </Typography>
                    {armocromia.subSeason && (
                      <Typography
                        sx={{
                          fontSize: '15px',
                          color: '#764ba2',
                          fontWeight: 700,
                          mt: 0.5,
                          mb: 2,
                          position: 'relative',
                          animation: 'fadeInUp 0.6s 0.35s ease-out backwards',
                          '@keyframes fadeInUp': {
                            '0%': { opacity: 0, transform: 'translateY(10px)' },
                            '100%': { opacity: 1, transform: 'translateY(0)' }
                          }
                        }}
                      >
                        {armocromia.subSeason}
                      </Typography>
                    )}

                    {/* Observed physical traits — pills with animation */}
                    {(armocromia.skinTone || armocromia.hairColor || armocromia.eyeColor) && (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: 0.8, mb: 2, position: 'relative' }}>
                        {[
                          { label: 'Pelle', value: armocromia.skinTone, icon: '◐' },
                          { label: 'Capelli', value: armocromia.hairColor, icon: '◉' },
                          { label: 'Occhi', value: armocromia.eyeColor, icon: '◎' }
                        ].filter(t => t.value).map((trait, i) => (
                          <Box
                            key={trait.label}
                            sx={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: 0.7,
                              fontSize: '12px',
                              fontWeight: 600,
                              background: 'linear-gradient(135deg, rgba(102,126,234,0.08), rgba(118,75,162,0.08))',
                              border: '1px solid rgba(102,126,234,0.15)',
                              borderRadius: '999px',
                              px: 1.5,
                              py: 0.6,
                              animation: `traitIn 0.5s ${0.5 + i * 0.1}s ease-out backwards`,
                              '@keyframes traitIn': {
                                '0%': { opacity: 0, transform: 'translateY(8px)' },
                                '100%': { opacity: 1, transform: 'translateY(0)' }
                              }
                            }}
                          >
                            <Box sx={{ color: '#764ba2', fontSize: '14px' }}>{trait.icon}</Box>
                            <Box sx={{ color: '#888', fontWeight: 500 }}>{trait.label}:</Box>
                            <Box sx={{ color: '#444' }}>{trait.value}</Box>
                          </Box>
                        ))}
                      </Box>
                    )}

                    {armocromia.undertone && (
                      <Box sx={{ display: 'inline-flex', gap: 1, mb: 2, position: 'relative', animation: 'fadeInUp 0.5s 0.85s ease-out backwards' }}>
                        <Box sx={{ fontSize: '11px', fontWeight: 700, color: '#667eea', background: 'rgba(102,126,234,0.1)', borderRadius: '8px', px: 1.2, py: 0.4, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                          Sottotono {armocromia.undertone === 'warm' ? 'caldo' : 'freddo'}
                        </Box>
                        {armocromia.contrast && (
                          <Box sx={{ fontSize: '11px', fontWeight: 700, color: '#667eea', background: 'rgba(102,126,234,0.1)', borderRadius: '8px', px: 1.2, py: 0.4, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                            Contrasto {armocromia.contrast === 'low' ? 'basso' : armocromia.contrast === 'medium' ? 'medio' : 'alto'}
                          </Box>
                        )}
                      </Box>
                    )}

                    <Typography
                      sx={{
                        fontSize: '14px',
                        color: '#555',
                        lineHeight: 1.7,
                        mb: 3,
                        maxWidth: '420px',
                        mx: 'auto',
                        textAlign: 'center',
                        position: 'relative',
                        animation: 'fadeInUp 0.6s 0.95s ease-out backwards'
                      }}
                    >
                      {armocromia.description}
                    </Typography>

                    {/* Consigli slider */}
                    {shuffledConsigli.current.length > 0 && (() => {
                      const current = shuffledConsigli.current[consiglioIdx] || {};
                      const slideDir = consiglioDirection === 'next' ? '30px' : '-30px';
                      return (
                        <Box
                          sx={{
                            mt: 2,
                            p: 2.5,
                            background: 'linear-gradient(135deg, rgba(102,126,234,0.06), rgba(240,147,251,0.06))',
                            border: '1px solid rgba(102,126,234,0.15)',
                            borderRadius: '18px',
                            overflow: 'hidden',
                            animation: 'fadeInUp 0.6s 1.6s ease-out backwards'
                          }}
                        >
                          <Typography sx={{ fontSize: '10px', fontWeight: 700, color: '#667eea', textTransform: 'uppercase', letterSpacing: '1.5px', mb: 1.5, textAlign: 'left' }}>
                            Consigli per te
                          </Typography>

                          {/* Slide content */}
                          <Box
                            sx={{
                              minHeight: '110px',
                              display: 'flex',
                              alignItems: 'flex-start',
                              position: 'relative',
                              overflow: 'hidden'
                            }}
                          >
                            <Box
                              key={consiglioIdx}
                              sx={{
                                width: '100%',
                                opacity: consiglioVisible ? 1 : 0,
                                transform: consiglioVisible ? 'translateX(0)' : `translateX(${consiglioDirection === 'next' ? '-' : ''}${slideDir})`,
                                transition: 'opacity 0.35s ease, transform 0.45s cubic-bezier(0.2, 0.9, 0.3, 1.1)',
                                display: 'flex',
                                gap: 1.5,
                                alignItems: 'flex-start',
                                textAlign: 'left'
                              }}
                            >
                              {/* Icon bubble */}
                              <Box
                                sx={{
                                  width: 44,
                                  height: 44,
                                  borderRadius: '14px',
                                  background: 'linear-gradient(135deg, #667eea, #764ba2)',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  fontSize: '22px',
                                  flexShrink: 0,
                                  boxShadow: '0 4px 14px rgba(102,126,234,0.3)'
                                }}
                              >
                                {current.icon}
                              </Box>
                              <Box sx={{ flex: 1, minWidth: 0 }}>
                                <Typography
                                  sx={{
                                    fontSize: '15px',
                                    fontWeight: 800,
                                    color: '#222',
                                    lineHeight: 1.2,
                                    mb: 0.6
                                  }}
                                >
                                  {current.title}
                                </Typography>
                                <Typography sx={{ fontSize: '13px', color: '#555', lineHeight: 1.55 }}>
                                  {current.text}
                                </Typography>
                              </Box>
                            </Box>
                          </Box>

                          {/* Navigation controls */}
                          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 0.5, mt: 1.5 }}>
                            <IconButton
                              onClick={() => goToConsiglio(-1)}
                              size="small"
                              sx={{
                                color: '#999',
                                background: 'rgba(102,126,234,0.08)',
                                '&:hover': { color: '#667eea', background: 'rgba(102,126,234,0.15)' }
                              }}
                            >
                              <ChevronLeftIcon fontSize="small" />
                            </IconButton>
                            {/* Progress dots */}
                            <Box sx={{ display: 'flex', gap: 0.5, mx: 1.5 }}>
                              {Array.from({ length: Math.min(shuffledConsigli.current.length, 10) }).map((_, i) => (
                                <Box
                                  key={i}
                                  sx={{
                                    width: i === consiglioIdx % 10 ? 16 : 5,
                                    height: 5,
                                    borderRadius: '3px',
                                    backgroundColor: i === consiglioIdx % 10 ? '#667eea' : 'rgba(102,126,234,0.2)',
                                    transition: 'width 0.35s ease, background-color 0.35s ease'
                                  }}
                                />
                              ))}
                            </Box>
                            <IconButton
                              onClick={() => goToConsiglio(1)}
                              size="small"
                              sx={{
                                color: '#999',
                                background: 'rgba(102,126,234,0.08)',
                                '&:hover': { color: '#667eea', background: 'rgba(102,126,234,0.15)' }
                              }}
                            >
                              <ChevronRightIcon fontSize="small" />
                            </IconButton>
                          </Box>
                        </Box>
                      );
                    })()}
                  </CardContent>
                </Card>
              </Box>
            )}

            {/* Action buttons */}
            <Box
              sx={{
                px: { xs: 2, sm: 3 },
                pt: 2,
                display: 'flex',
                flexDirection: 'column',
                gap: 2
              }}
            >
              <Button
                fullWidth
                variant="contained"
                startIcon={<QrCodeScannerIcon />}
                onClick={() => navigate('/scan')}
                sx={{
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  color: 'white',
                  padding: '16px',
                  fontSize: '16px',
                  fontWeight: 700,
                  borderRadius: '14px',
                  textTransform: 'none',
                  boxShadow: '0 8px 25px rgba(102, 126, 234, 0.3)',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #764ba2 0%, #667eea 100%)'
                  }
                }}
              >
                Scannerizza altro capo
              </Button>

              <Box sx={{ display: 'flex', gap: 2 }}>
                <Button
                  fullWidth
                  variant="outlined"
                  onClick={toggleSave}
                  sx={{
                    borderColor: '#667eea',
                    color: '#667eea',
                    padding: '14px',
                    fontSize: '15px',
                    fontWeight: 600,
                    borderRadius: '14px',
                    textTransform: 'none',
                    border: '2px solid #667eea'
                  }}
                >
                  {isSaved ? 'Salvato' : 'Salva'}
                </Button>
                <Button
                  fullWidth
                  variant="outlined"
                  onClick={handleShare}
                  sx={{
                    borderColor: '#667eea',
                    color: '#667eea',
                    padding: '14px',
                    fontSize: '15px',
                    fontWeight: 600,
                    borderRadius: '14px',
                    textTransform: 'none',
                    border: '2px solid #667eea'
                  }}
                >
                  Condividi
                </Button>
              </Box>
            </Box>
          </Box>
        </>
      )}

      {/* Share menu — bottom sheet opened by tapping the try-on photo */}
      <Drawer
        anchor="bottom"
        open={shareMenuOpen}
        onClose={() => { setShareMenuOpen(false); setShareError(null); }}
        PaperProps={{
          sx: {
            borderTopLeftRadius: '24px',
            borderTopRightRadius: '24px',
            pb: 'calc(16px + env(safe-area-inset-bottom))',
            background: '#fff'
          }
        }}
      >
        <Box sx={{ position: 'relative', pt: 2.5, px: 3 }}>
          <Box
            sx={{
              width: 40,
              height: 4,
              borderRadius: 2,
              background: 'rgba(0,0,0,0.12)',
              mx: 'auto',
              mb: 2
            }}
          />
          <IconButton
            onClick={() => setShareMenuOpen(false)}
            size="small"
            sx={{ position: 'absolute', top: 10, right: 10, color: '#999' }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
          <Typography sx={{ fontSize: '17px', fontWeight: 700, color: '#222', mb: 0.5 }}>
            Condividi la tua prova
          </Typography>
          <Typography sx={{ fontSize: '13px', color: '#888', mb: 2.5 }}>
            Salvala, inviala agli amici o condividila
          </Typography>
          {shareError && (
            <Alert severity="error" onClose={() => setShareError(null)} sx={{ mb: 2, borderRadius: '12px', fontSize: '13px' }}>
              {shareError}
            </Alert>
          )}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {[
              { key: 'save', icon: <DownloadIcon />, title: 'Salva in galleria', subtitle: 'Scarica la foto sul tuo dispositivo', color: '#667eea' },
              { key: 'whatsapp', icon: <WhatsAppIcon />, title: 'Invia su WhatsApp', subtitle: 'Condividi con i tuoi amici', color: '#25D366' },
              { key: 'share', icon: <IosShareIcon />, title: 'Condividi sui social', subtitle: 'Instagram, TikTok, Messages…', color: '#764ba2' }
            ].map(action => {
              const isThisPending = sharePendingAction === action.key;
              const isAnyPending = !!sharePendingAction;
              return (
                <Box
                  key={action.key}
                  onClick={() => { if (!isAnyPending) handleMenuAction(action.key); }}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 2,
                    p: 1.5,
                    borderRadius: '14px',
                    cursor: isAnyPending ? 'default' : 'pointer',
                    opacity: isAnyPending && !isThisPending ? 0.4 : 1,
                    transition: 'background 0.15s ease, opacity 0.2s ease',
                    '&:hover': isAnyPending ? {} : { background: 'rgba(102,126,234,0.06)' },
                    '&:active': isAnyPending ? {} : { background: 'rgba(102,126,234,0.12)' }
                  }}
                >
                  <Box
                    sx={{
                      width: 44,
                      height: 44,
                      borderRadius: '12px',
                      background: `${action.color}15`,
                      color: action.color,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0
                    }}
                  >
                    {isThisPending ? <CircularProgress size={20} sx={{ color: action.color }} /> : action.icon}
                  </Box>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography sx={{ fontSize: '15px', fontWeight: 700, color: '#222', lineHeight: 1.2 }}>
                      {isThisPending ? 'Preparazione immagine…' : action.title}
                    </Typography>
                    <Typography sx={{ fontSize: '12px', color: '#888', mt: 0.3 }}>
                      {isThisPending ? 'Un attimo, sto ottimizzando la foto per la condivisione' : action.subtitle}
                    </Typography>
                  </Box>
                </Box>
              );
            })}
          </Box>
        </Box>
      </Drawer>

      {/* Try-or-buy dialog — opens on recommendation card tap */}
      <Drawer
        anchor="bottom"
        open={!!recDialogProduct}
        onClose={() => setRecDialogProduct(null)}
        PaperProps={{
          sx: {
            borderTopLeftRadius: '24px',
            borderTopRightRadius: '24px',
            pb: 'calc(16px + env(safe-area-inset-bottom))',
            background: '#fff'
          }
        }}
      >
        {recDialogProduct && (
          <Box sx={{ position: 'relative', pt: 2.5, px: 3 }}>
            <Box
              sx={{
                width: 40,
                height: 4,
                borderRadius: 2,
                background: 'rgba(0,0,0,0.12)',
                mx: 'auto',
                mb: 2
              }}
            />
            <IconButton
              onClick={() => setRecDialogProduct(null)}
              size="small"
              sx={{ position: 'absolute', top: 10, right: 10, color: '#999' }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>

            {/* Product preview */}
            <Box sx={{ display: 'flex', gap: 2, mb: 2.5, alignItems: 'center' }}>
              <Box
                sx={{
                  width: 88,
                  height: 110,
                  borderRadius: '14px',
                  overflow: 'hidden',
                  background: '#f5f5f5',
                  flexShrink: 0
                }}
              >
                <img
                  src={getShopifyThumb(recDialogProduct.imageUrl || recDialogProduct.immagine_url, 400)}
                  alt={recDialogProduct.name || recDialogProduct.nome}
                  style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                />
              </Box>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography sx={{ fontSize: '15px', fontWeight: 700, color: '#222', lineHeight: 1.3, mb: 0.5 }}>
                  {recDialogProduct.name || recDialogProduct.nome}
                </Typography>
                {recDialogProduct.brand && (
                  <Typography sx={{ fontSize: '12px', color: '#888', mb: 0.5 }}>
                    {recDialogProduct.brand}
                  </Typography>
                )}
                <Typography sx={{ fontSize: '17px', fontWeight: 800, color: '#667eea' }}>
                  €{typeof (recDialogProduct.price ?? recDialogProduct.prezzo) === 'number'
                    ? (recDialogProduct.price ?? recDialogProduct.prezzo).toFixed(2)
                    : (recDialogProduct.price ?? recDialogProduct.prezzo)}
                </Typography>
              </Box>
            </Box>

            {/* Actions — use inline style for layout-critical properties so MUI's
                 variant-specific padding/border (contained vs outlined) can't
                 shift the boxes relative to each other. */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25, pb: 1 }}>
              {(() => {
                const baseStyle = {
                  boxSizing: 'border-box',
                  width: '100%',
                  padding: '12px',
                  fontSize: '15px',
                  fontWeight: 700,
                  borderRadius: '14px',
                  textTransform: 'none',
                  borderWidth: '2px',
                  borderStyle: 'solid',
                  lineHeight: 1.5,
                  margin: 0
                };
                const primaryStyle = {
                  ...baseStyle,
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  color: '#fff',
                  borderColor: 'transparent',
                  boxShadow: '0 6px 20px rgba(102,126,234,0.35)'
                };
                const brandStyle = {
                  ...baseStyle,
                  color: '#667eea',
                  background: '#fff',
                  borderColor: '#667eea',
                  boxShadow: 'none'
                };
                const buyStyle = {
                  ...baseStyle,
                  color: '#1a1a1a',
                  background: '#fff',
                  borderColor: '#1a1a1a',
                  boxShadow: 'none'
                };
                return (
                  <>
                    {userPhoto && (
                      <Button
                        disableRipple
                        disableElevation
                        variant="contained"
                        onClick={() => {
                          const rec = recDialogProduct;
                          setRecDialogProduct(null);
                          // Normalize DB keys (italian → english) so server gets a usable imageUrl
                          localStorage.setItem('currentProduct', JSON.stringify({
                            ...rec,
                            imageUrl: rec.imageUrl || rec.immagine_url
                          }));
                          // Drop any stale pre-started try-on from the previous product
                          const staleSid = localStorage.getItem('tryOnSessionId');
                          if (staleSid) {
                            clearCachedTryOn(staleSid);
                            localStorage.removeItem('tryOnSessionId');
                          }
                          navigate(`/try-on-result?p=${rec.id}`);
                        }}
                        style={primaryStyle}
                      >
                        Prova con la foto attuale
                      </Button>
                    )}
                    <Button
                      disableRipple
                      disableElevation
                      variant={userPhoto ? 'outlined' : 'contained'}
                      onClick={() => {
                        const rec = recDialogProduct;
                        setRecDialogProduct(null);
                        localStorage.setItem('currentProduct', JSON.stringify({
                          ...rec,
                          imageUrl: rec.imageUrl || rec.immagine_url
                        }));
                        // Skip the old product-landing page entirely — go straight to photo capture
                        navigate(`/photo-capture?p=${rec.id}`);
                      }}
                      style={userPhoto ? brandStyle : primaryStyle}
                    >
                      Scatta una nuova foto
                    </Button>
                    {/^https?:\/\//i.test(recDialogProduct.url_originale || '') && (
                      <Button
                        disableRipple
                        disableElevation
                        variant="outlined"
                        startIcon={<ShoppingBagOutlinedIcon />}
                        onClick={() => {
                          window.open(recDialogProduct.url_originale, '_blank', 'noopener,noreferrer');
                        }}
                        style={buyStyle}
                      >
                        Acquista su Piazza Italia
                      </Button>
                    )}
                  </>
                );
              })()}
            </Box>
          </Box>
        )}
      </Drawer>
    </Box>
  );
}

export default TryOnResult;
